/**
 * Root Cause Analysis Tab
 * Displays AI-powered root cause analysis for anomalies
 */

import React, { useState } from 'react';
import { Anomaly } from '../../services/anomalyService';
import { OpenTicketButton } from '../tickets/OpenTicketButton';
import { getActiveTenantId } from '../../services/tenantContext';
import '../../styles/RCAAnalysisTab.css';
import { StepTimeline, useInvestigationSocket } from '../investigation/StepTimeline';

interface RCAAnalysisTabProps {
  anomaly: Anomaly;
  onRefresh?: () => void;
}

interface RCACause {
  rank: number;
  cause: string;
  confidence: number;
  contribution_percent: number;
}

interface RCAEvidence {
  cost_delta: number;
  cost_delta_percent: number;
  detection_method?: string;
  detector_votes: string[];
  multi_signal_confirmed: boolean;
  baseline_mean?: number;
  baseline_std_dev?: number;
  // Requirement 41 additions - optional so a previously-cached RCA result
  // (generated before this pass) still renders without these fields.
  related_recommendations?: { recommendation_type: string; resource_id: string; confidence: string; potential_savings: number | null }[];
  deterministic_candidates?: { cause: string; correlation: number; impact: number; confidence: number; score: number }[];
  reconciliation?: { within_tolerance: boolean; contribution_total_percent: number; deviation_percent: number };
}

// Requirement 46.2/46.3 - real per-claim citations computed by
// grounding-check.service.ts, optional so a previously-cached RCA result
// (generated before this pass) still renders without it.
interface RCAGrounding {
  grounded: boolean;
  confidence: number;
  totalClaims: number;
  ungroundedClaims: string[];
  citations: { claim: string; sources: string[] }[];
}

// Requirement 42 - only present when the top deterministic candidate was
// uncertain enough (score < 0.7) to trigger a real sandboxed validation query.
interface HypothesisValidation {
  hypothesis: string;
  validationQuery: string;
  querySucceeded: boolean;
  rowCount?: number;
  corroborated: boolean | null;
  reason: string;
}

// FR-RCA-010 - 3 analyst follow-up questions, each wired to a real,
// deterministic action (never an LLM-invented one) - optional so a
// previously-cached RCA result still renders without it.
interface RCAFollowUpQuestion {
  question: string;
  action_type: 'confirm_cause' | 'investigate';
  action_ref: string;
}

interface RCAResult {
  summary: string;
  causes: RCACause[];
  evidence: RCAEvidence;
  investigation_steps: string[];
  prevention: string[];
  confidence: number;
  generated_at: string;
  model: string;
  // Requirement 44.3 - which LLM tier served this narrative (see
  // llm-tier-router.service.ts); optional so a previously-cached RCA result
  // (generated before this pass) still renders without it.
  tier?: string;
  grounding?: RCAGrounding;
  hypothesis_validation?: HypothesisValidation;
  follow_up_questions?: RCAFollowUpQuestion[];
}

interface RemediationClassification {
  tier: 'L1' | 'L2' | 'L3';
  reasoning: string;
  triggered_by: 'guard' | 'llm' | 'fallback';
}

interface GeneratedSOP {
  title: string;
  problem_summary: string;
  identification_steps: string[];
  resolution_steps: string[];
  verification_steps: string[];
}

interface DryRunAction {
  resource_id: string;
  action_verb: string;
  description: string;
  estimated_monthly_savings: number | null;
}

interface TicketOutcome {
  id: string;
  external_key: string | null;
  external_url: string | null;
  status: string;
}

interface InvestigationResult {
  classification?: RemediationClassification;
  sop?: GeneratedSOP | { error: string };
  dry_run_action?: DryRunAction;
  /** Requirement 210 - the real runnable command attached to this L1 finding. */
  remediation_script?: { script_text: string | null; script_error: string | null; script_source?: 'template' | 'llm' | null };
  /** Requirement 49 AC3/AC4 - the real execution outcome once L1 execution was attempted. */
  l1_execution?: { status: 'executed' | 'blocked' | 'failed'; block_reason?: string; audit_id: string | null };
  ticket?: TicketOutcome | null;
  errors: { step: string; message: string }[];
}

const RCAAnalysisTab: React.FC<RCAAnalysisTabProps> = ({ anomaly, onRefresh: _onRefresh }) => {
  const [rca, setRCA] = useState<RCAResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [investigation, setInvestigation] = useState<InvestigationResult | null>(null);
  const [investigating, setInvestigating] = useState(false);
  const [investigationError, setInvestigationError] = useState<string | null>(null);
  // Requirement 41.5 - real ground-truth labeling of the primary (rank 1)
  // cause, feeding /admin/rca-accuracy-metrics. undefined = not labeled yet
  // this session, true/false = the confirmation just recorded.
  const [causeLabel, setCauseLabel] = useState<boolean | undefined>(undefined);
  // AC-RCA-003 - "was this analysis useful" feedback. undefined = not rated
  // yet this session, true/false = the rating just recorded.
  const [usefulnessRated, setUsefulnessRated] = useState<boolean | undefined>(undefined);

  // Live progress of the real RCA LangGraph. Subscribed for as long as this
  // tab is mounted rather than only while a request is in flight: an
  // authenticated socket takes a JWT verify plus a tenant-authorization
  // lookup to establish, which is slower than the first graph nodes take to
  // run - connecting on click meant the early events were already gone.
  const graphSteps = useInvestigationSocket(anomaly.id, true);

  const handleRateUsefulness = async (useful: boolean) => {
    setUsefulnessRated(useful);
    try {
      const apiUrl = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
      const token = localStorage.getItem('accessToken');
      await fetch(`${apiUrl}/api/v1/anomalies/${anomaly.id}/rca/rate-usefulness`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': getActiveTenantId(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ useful }),
      });
    } catch (e) {
      // Best-effort - a failed rating shouldn't disrupt the RCA view itself.
      console.warn('Failed to record usefulness rating', e);
    }
  };

  const handleConfirmCause = async (rank: number, cause: string, confirmed: boolean) => {
    setCauseLabel(confirmed);
    try {
      const apiUrl = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
      const token = localStorage.getItem('accessToken');
      await fetch(`${apiUrl}/api/v1/anomalies/${anomaly.id}/rca/confirm-cause`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': getActiveTenantId(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ rank, cause, confirmed }),
      });
    } catch (e) {
      // Best-effort - a failed label shouldn't disrupt the RCA view itself.
      console.warn('Failed to record cause confirmation', e);
    }
  };

  const handleAnalyze = async () => {
    if (loading) return; // Prevent double-click
    
    setLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${apiUrl}/api/v1/anomalies/${anomaly.id}/analyze-rca`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-tenant-id': getActiveTenantId(),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to generate RCA analysis');
      }

      // Set RCA data immediately - DO NOT refresh parent
      setRCA(data.data);
      
      // DO NOT call onRefresh here - it causes modal to re-render
      // The RCA is already saved to database, no need to refresh
    } catch (err: any) {
      setError(err.message || 'Failed to generate RCA analysis');
      console.error('RCA failed:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Requirement 41/49/53/55 + net-new SOP: runs the fuller investigation
   * pipeline (investigation-orchestrator.service.ts) on top of the RCA
   * that's already been generated - separate from handleAnalyze/regenerate
   * so a user can inspect the plain RCA first, then opt into the classify
   * + SOP + act step.
   */
  const handleInvestigate = async () => {
    if (investigating) return;
    setInvestigating(true);
    setInvestigationError(null);

    try {
      const apiUrl = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${apiUrl}/api/v1/anomalies/${anomaly.id}/investigate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': getActiveTenantId(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to run investigation');
      }

      setInvestigation(data.data);
      // The orchestrator re-runs RCA internally with the fuller evidence
      // bundle (related recommendations, deterministic candidates,
      // reconciliation) - reflect that in the RCA section above too rather
      // than leaving it showing the earlier, thinner result.
      if (data.data.rca) setRCA(data.data.rca);
    } catch (err: any) {
      setInvestigationError(err.message || 'Failed to run investigation');
    } finally {
      setInvestigating(false);
    }
  };

  const tierColor = (tier: string) => {
    if (tier === 'L1') return '#10B981';
    if (tier === 'L2') return '#F59E0B';
    return '#EF4444';
  };

  // Format date
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get confidence color
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return '#10B981'; // Green
    if (confidence >= 0.6) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  // Show error state
  if (error) {
    return (
      <div className="rca-tab">
        <div className="rca-error">
          <div className="error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3>Failed to Generate Analysis</h3>
          <p>{error}</p>
          <button onClick={handleAnalyze} disabled={loading} className="btn-retry">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show generate state (no RCA yet)
  if (!rca) {
    return (
      <div className="rca-tab">
        <div className="rca-empty">
          <div className="empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <h3>Generate Root Cause Analysis</h3>
          <p className="empty-description">
            Use AI to analyze this anomaly and identify the specific resources,
            configurations, or actions that caused the cost spike.
          </p>
          {/* The three explainer cards are useful BEFORE you click and pure
              noise once the pipeline is running - and while they were on screen
              they pushed the live timeline below the fold, which is why the
              steps looked like they never appeared. They now yield to it. */}
          {graphSteps.length === 0 && !loading && (
            <div className="rca-features">
              <div className="feature-item">
                <div className="feature-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <strong>Identify Root Causes</strong>
                  <p>Pinpoint which resources or services triggered the anomaly</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <strong>Investigation Steps</strong>
                  <p>Get specific portal links and queries to verify the issue</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <strong>Prevention Measures</strong>
                  <p>Receive actionable recommendations to prevent recurrence</p>
                </div>
              </div>
            </div>
          )}
          {/* The real graph, live — rendered ABOVE the button on purpose.
              It sat below the button and the loading text before, which put it
              off-screen for the whole run on a laptop: the steps were arriving
              correctly and simply could not be seen without scrolling.

              Each row is a node that actually ran, with its duration computed
              from the server's own paired started/completed timestamps. The
              three evidence collectors are dispatched together via LangGraph
              Send(), so they appear and finish concurrently rather than in a
              queue - which is the whole reason to show this instead of a
              spinner. */}
          {graphSteps.length > 0 && (
            <div className="mb-5 w-full max-w-xl rounded-lg border bg-card p-4 text-left shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Investigation pipeline
                </span>
                <span className="text-2xs text-muted-foreground">
                  {graphSteps.filter((s) => s.status === 'completed').length} of {graphSteps.length} complete
                </span>
              </div>
              <StepTimeline steps={graphSteps} showParallelHint />
            </div>
          )}

          {/* Only shown in the gap before the first step event arrives -
              typically a few hundred milliseconds. */}
          {loading && graphSteps.length === 0 && (
            <p className="loading-text" style={{ marginBottom: '16px' }}>
              Starting investigation pipeline…
            </p>
          )}

          <button
            onClick={handleAnalyze}
            disabled={loading}
            className="btn-generate"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Analyzing with AI...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate Root Cause Analysis
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Show RCA results
  return (
    <div className="rca-tab">
      <div className="rca-content">
        {/* The pipeline that produced everything below. Kept visible after the
            run (collapsed by default) rather than thrown away: it is the
            provenance of the analysis - which evidence was actually collected,
            which nodes degraded, and how long each took. A reader asking
            "where did this come from?" should not have to re-run it. */}
        {graphSteps.length > 0 && (
          <details className="rca-section" style={{ marginBottom: '16px' }}>
            <summary
              style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.7 }}
            >
              Investigation pipeline — {graphSteps.length} step
              {graphSteps.length === 1 ? '' : 's'}
              {graphSteps.some((s) => s.status === 'failed') && ' (some degraded)'}
            </summary>
            <div style={{ marginTop: '12px' }}>
              <StepTimeline steps={graphSteps} showParallelHint />
            </div>
          </details>
        )}

        {/* Summary Section */}
        <div className="rca-section rca-summary">
          <div className="section-header">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Summary
            </h3>
            <div
              className="confidence-badge"
              style={{ backgroundColor: getConfidenceColor(rca.confidence) }}
            >
              {(rca.confidence * 100).toFixed(0)}% Confidence
            </div>
          </div>
          <p className="summary-text">{rca.summary}</p>

          {/* Requirement 46.2/46.3: real grounding status + per-claim
              citation footnotes, computed by grounding-check.service.ts
              from the actual evidence given to the LLM - not a fabricated
              confidence indicator. */}
          {rca.grounding && (
            <div style={{ marginTop: '12px' }}>
              {!rca.grounding.grounded && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    background: 'rgba(245,158,11,0.1)',
                    border: '1px solid rgba(245,158,11,0.35)',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    marginBottom: '10px',
                    fontSize: '13px',
                  }}
                >
                  <span style={{ fontWeight: 700, color: '#F59E0B' }}>⚠ Low confidence:</span>
                  <span style={{ color: '#92400E' }}>
                    {rca.grounding.ungroundedClaims.length} claim(s) in this narrative could not be traced back to
                    real evidence, even after one automatic rewrite attempt. Treat this analysis as a starting point,
                    not a verified conclusion.
                  </span>
                </div>
              )}
              {rca.grounding.citations.length > 0 && (
                <details style={{ fontSize: '12px', color: '#64748b' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                    Citations ({rca.grounding.citations.length} claim{rca.grounding.citations.length === 1 ? '' : 's'} traced to evidence)
                  </summary>
                  <ol style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                    {rca.grounding.citations.map((c, i) => (
                      <li key={i} style={{ marginBottom: '4px' }}>
                        <span>{c.claim}</span>
                        <span style={{ color: '#94a3b8' }}> — [{c.sources.join(', ')}]</span>
                      </li>
                    ))}
                  </ol>
                </details>
              )}
            </div>
          )}

          {/* Requirement 42 - only rendered when the top deterministic
              candidate was uncertain enough to trigger a real sandboxed
              validation query (not shown for confident RCAs). */}
          {rca.hypothesis_validation && (
            <div style={{ marginTop: '10px' }}>
              {rca.hypothesis_validation.querySucceeded ? (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: rca.hypothesis_validation.corroborated ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.1)',
                    color: rca.hypothesis_validation.corroborated ? '#059669' : '#DC2626',
                  }}
                  title={rca.hypothesis_validation.reason}
                >
                  Hypothesis: "{rca.hypothesis_validation.hypothesis}" — {rca.hypothesis_validation.corroborated ? 'validated ✓' : 'unconfirmed'}
                  {rca.hypothesis_validation.rowCount !== undefined ? ` (${rca.hypothesis_validation.rowCount} matching row${rca.hypothesis_validation.rowCount === 1 ? '' : 's'})` : ''}
                </span>
              ) : (
                <span style={{ fontSize: '12px', color: '#94a3b8' }} title={rca.hypothesis_validation.reason}>
                  Hypothesis validation attempted but the query could not run - see details
                </span>
              )}
            </div>
          )}
        </div>

        {/* Ranked Causes Section */}
        <div className="rca-section rca-causes">
          <div className="section-header">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Ranked Root Causes
            </h3>
          </div>
          <ol className="causes-list">
            {rca.causes.map((c) => (
              <li key={c.rank} className="cause-item">
                <span className="cause-number">{c.rank}</span>
                <div style={{ flex: 1 }}>
                  <span className="cause-text">{c.cause}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: 'rgba(148,163,184,0.25)', overflow: 'hidden' }}>
                      <div style={{ width: `${c.contribution_percent}%`, height: '100%', background: getConfidenceColor(c.confidence) }} />
                    </div>
                    <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {c.contribution_percent.toFixed(0)}% of delta &middot; {(c.confidence * 100).toFixed(0)}% confidence
                    </span>
                  </div>
                  {/* Requirement 41.5 - only the primary (rank 1) cause is
                      labeled; the other candidates are supporting context,
                      not individually-asserted conclusions. */}
                  {c.rank === 1 && (
                    <div style={{ marginTop: '6px', fontSize: '12px' }}>
                      {causeLabel === undefined ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                          Was this correct?
                          <button onClick={() => handleConfirmCause(c.rank, c.cause, true)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px' }} title="Confirm this cause was correct">👍</button>
                          <button onClick={() => handleConfirmCause(c.rank, c.cause, false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px' }} title="This cause was wrong">👎</button>
                        </span>
                      ) : (
                        <span style={{ color: '#64748b' }}>{causeLabel ? '✓ Marked correct - thanks' : '✗ Marked incorrect - thanks'}</span>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Evidence Section - real, deterministic facts, not LLM narrative */}
        <div className="rca-section rca-evidence">
          <div className="section-header">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Evidence
            </h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Cost delta</div>
              <div style={{ fontWeight: 600 }}>${rca.evidence.cost_delta.toFixed(2)} ({rca.evidence.cost_delta_percent}%)</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Detection method</div>
              <div style={{ fontWeight: 600 }}>{rca.evidence.detection_method || 'n/a'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Detector agreement</div>
              <div style={{ fontWeight: 600 }}>
                {rca.evidence.detector_votes.length > 0 ? rca.evidence.detector_votes.join(', ') : 'single detector'}
                {rca.evidence.multi_signal_confirmed && (
                  <span style={{ marginLeft: '6px', fontSize: '11px', color: '#10B981' }}>&#10003; confirmed</span>
                )}
              </div>
            </div>
            {rca.evidence.baseline_mean !== undefined && (
              <div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>30-day baseline mean</div>
                <div style={{ fontWeight: 600 }}>${rca.evidence.baseline_mean.toFixed(2)}</div>
              </div>
            )}
            {rca.evidence.reconciliation && (
              <div>
                {/* Named for what this actually measures. The ranked causes'
                    contribution percentages are normalised to total 100 before
                    this check runs, so a passing result confirms the shares are
                    internally consistent - it is NOT an independent verification
                    that the causes account for the real cost delta. Labelling it
                    "% of delta explained" overstated it. */}
                <div style={{ fontSize: '12px', color: '#64748b' }}>Cause shares total (&plusmn;3%)</div>
                <div style={{ fontWeight: 600, color: rca.evidence.reconciliation.within_tolerance ? '#10B981' : '#EF4444' }}>
                  {rca.evidence.reconciliation.contribution_total_percent}%
                  {rca.evidence.reconciliation.within_tolerance ? ' ✓' : ` (off by ${rca.evidence.reconciliation.deviation_percent}pp)`}
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                  Internal consistency of the ranked shares, not an independent check against billing.
                </div>
              </div>
            )}
          </div>
          {rca.evidence.related_recommendations && rca.evidence.related_recommendations.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Related open recommendations</div>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
                {rca.evidence.related_recommendations.map((r, i) => (
                  <li key={i}>
                    {r.recommendation_type} on {r.resource_id}
                    {r.potential_savings ? ` · $${r.potential_savings.toFixed(2)}/mo` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Investigation Section - Requirement 41/49/53/55 + net-new SOP:
            classification, generated SOP, and either the L1 dry-run action
            or the L2/L3 ticket outcome. Separate step from the RCA above -
            a user can read the RCA first, then opt into this. */}
        <div className="rca-section">
          <div className="section-header">
            <h3>Investigation &amp; Remediation</h3>
          </div>
          {!investigation ? (
            <div>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px' }}>
                Classify this anomaly's risk tier, generate a Standard Operating Procedure, and either compute the
                dry-run action (L1) or open a ticket with the SOP attached (L2/L3).
              </p>
              <button onClick={handleInvestigate} disabled={investigating} className="btn-generate">
                {investigating ? (
                  <>
                    <span className="spinner"></span>
                    Investigating...
                  </>
                ) : (
                  'Run Full Investigation'
                )}
              </button>
              {investigationError && (
                <p style={{ color: '#EF4444', fontSize: '13px', marginTop: '8px' }}>{investigationError}</p>
              )}
            </div>
          ) : (
            <div>
              {investigation.classification && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <span
                    className="confidence-badge"
                    style={{ backgroundColor: tierColor(investigation.classification.tier) }}
                  >
                    {investigation.classification.tier}
                  </span>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>{investigation.classification.reasoning}</span>
                </div>
              )}

              {investigation.classification?.tier === 'L1' && investigation.dry_run_action && (
                <div
                  style={{
                    background: investigation.l1_execution?.status === 'executed' ? 'rgba(37,99,235,0.08)' : 'rgba(16,185,129,0.08)',
                    border: investigation.l1_execution?.status === 'executed' ? '1px solid rgba(37,99,235,0.35)' : '1px solid rgba(16,185,129,0.35)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '12px',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em', color: investigation.l1_execution?.status === 'executed' ? '#2563EB' : investigation.l1_execution?.status === 'failed' ? '#DC2626' : '#10B981' }}>
                    {investigation.l1_execution?.status === 'executed'
                      ? 'Executed automatically — real action taken'
                      : investigation.l1_execution?.status === 'blocked'
                        ? 'Execution blocked by a safety guard'
                        : investigation.l1_execution?.status === 'failed'
                          ? 'Execution failed'
                          : 'Dry run only — no action taken'}
                  </div>
                  {investigation.l1_execution?.status && investigation.l1_execution.status !== 'executed' && investigation.l1_execution.block_reason && (
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>{investigation.l1_execution.block_reason}</p>
                  )}
                  {investigation.l1_execution?.audit_id && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#94a3b8' }}>Audit record: {investigation.l1_execution.audit_id}</p>
                  )}
                  <p style={{ margin: '6px 0 0' }}>{investigation.dry_run_action.description}</p>
                  {investigation.dry_run_action.estimated_monthly_savings != null && (
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                      Estimated savings: ${investigation.dry_run_action.estimated_monthly_savings.toFixed(2)}/mo
                    </p>
                  )}
                  {investigation.remediation_script?.script_text ? (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                          {investigation.l1_execution?.status === 'executed' ? 'Command executed' : 'Exact command (not executed)'}
                        </span>
                        <button
                          onClick={() => navigator.clipboard.writeText(investigation.remediation_script?.script_text || '')}
                          style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          Copy
                        </button>
                      </div>
                      <pre style={{ margin: '4px 0 0', background: '#0f172a', color: '#e2e8f0', borderRadius: '6px', padding: '10px', fontSize: '12px', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                        {investigation.remediation_script.script_text}
                      </pre>
                    </div>
                  ) : investigation.remediation_script?.script_error ? (
                    <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#94a3b8' }}>{investigation.remediation_script.script_error}</p>
                  ) : null}
                </div>
              )}

              {investigation.sop && !('error' in investigation.sop) && (
                <div className="rca-section" style={{ background: 'rgba(148,163,184,0.06)', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                  <h4 style={{ margin: '0 0 8px' }}>{investigation.sop.title}</h4>
                  <p style={{ fontSize: '13px', margin: '0 0 8px' }}>{investigation.sop.problem_summary}</p>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginTop: '8px' }}>IDENTIFICATION</div>
                  <ul style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '13px' }}>
                    {investigation.sop.identification_steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginTop: '8px' }}>RESOLUTION</div>
                  <ul style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '13px' }}>
                    {investigation.sop.resolution_steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginTop: '8px' }}>VERIFICATION</div>
                  <ul style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '13px' }}>
                    {investigation.sop.verification_steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {investigation.sop && 'error' in investigation.sop && (
                <p style={{ color: '#F59E0B', fontSize: '13px' }}>SOP generation unavailable: {investigation.sop.error}</p>
              )}

              {(investigation.classification?.tier === 'L2' || investigation.classification?.tier === 'L3') && (
                <div style={{ fontSize: '13px' }}>
                  {investigation.ticket?.external_url ? (
                    <a href={investigation.ticket.external_url} target="_blank" rel="noreferrer">
                      Ticket {investigation.ticket.external_key} opened ({investigation.ticket.status}) &rarr;
                    </a>
                  ) : (
                    <span style={{ color: '#F59E0B' }}>No ticket was created - check that Jira is configured for this tenant.</span>
                  )}
                </div>
              )}

              {investigation.errors.length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#94a3b8' }}>
                  {investigation.errors.map((e, i) => (
                    <div key={i}>Step "{e.step}" had an issue: {e.message}</div>
                  ))}
                </div>
              )}

              <button onClick={handleInvestigate} disabled={investigating} className="btn-retry" style={{ marginTop: '10px' }}>
                {investigating ? 'Re-running...' : 'Re-run Investigation'}
              </button>
            </div>
          )}
        </div>

        {/* Quick Actions - real navigation/actions, not generic "workflows" */}
        <div className="rca-section rca-quick-actions">
          <div className="section-header">
            <h3>Quick Actions</h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <a
              href={`/cost-explorer?provider=${anomaly.provider}`}
              className="btn-retry"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
            >
              View in Cost Explorer
            </a>
            <a
              href="/policies"
              className="btn-retry"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
            >
              View Policies
            </a>
            <OpenTicketButton
              sourceType="anomaly"
              sourceId={anomaly.id}
              summary={`Investigate anomaly: ${anomaly.service || ''} (${anomaly.provider})`}
              description={rca.summary}
              label="Open Investigation Ticket"
            />
          </div>
        </div>

        {/* Investigation Steps Section */}
        <div className="rca-section rca-investigation">
          <div className="section-header">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Investigation Steps
            </h3>
          </div>
          <ol className="investigation-list">
            {rca.investigation_steps.map((step, index) => (
              <li key={index} className="investigation-item">
                <span className="step-number">{index + 1}</span>
                <span className="step-text">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Prevention Section */}
        <div className="rca-section rca-prevention">
          <div className="section-header">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Prevention Measures
            </h3>
          </div>
          <ul className="prevention-list">
            {rca.prevention.map((action, index) => (
              <li key={index} className="prevention-item">
                <span className="prevention-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="prevention-text">{action}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Follow-up Questions - FR-RCA-010. Each question's action_type is
            assigned deterministically by the backend, never by the LLM, so
            these always resolve to a real action. */}
        {rca.follow_up_questions && rca.follow_up_questions.length > 0 && (
          <div className="rca-section rca-followup">
            <div className="section-header">
              <h3>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Follow-up Questions
              </h3>
            </div>
            <ol className="investigation-list">
              {rca.follow_up_questions.map((q, index) => (
                <li key={index} className="investigation-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                    <span className="step-number">{index + 1}</span>
                    <span className="step-text" style={{ flex: 1 }}>{q.question}</span>
                  </div>
                  <div style={{ marginLeft: '34px', marginTop: '6px' }}>
                    {q.action_type === 'confirm_cause' ? (
                      causeLabel === undefined ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#64748b' }}>
                          <button onClick={() => handleConfirmCause(1, rca.causes[0]?.cause || '', true)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px' }} title="Confirm the top cause was correct">👍 Yes</button>
                          <button onClick={() => handleConfirmCause(1, rca.causes[0]?.cause || '', false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px' }} title="The top cause was wrong">👎 No</button>
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#64748b' }}>{causeLabel ? '✓ Confirmed - thanks' : '✗ Marked incorrect - thanks'}</span>
                      )
                    ) : (
                      <button onClick={handleInvestigate} disabled={investigating} className="btn-retry" style={{ fontSize: '12px', padding: '4px 12px' }}>
                        {investigating ? 'Running...' : 'Run Investigation →'}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Metadata Footer */}
        <div className="rca-footer">
          <div className="rca-meta">
            <span className="meta-item">
              <strong>Generated:</strong> {formatDateTime(rca.generated_at)}
            </span>
            <span className="meta-item">
              <strong>Model:</strong> {rca.model}
              {rca.tier ? ` (${rca.tier})` : ''}
            </span>
            <span className="meta-item">
              {usefulnessRated === undefined ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Was this useful?
                  <button onClick={() => handleRateUsefulness(true)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px' }} title="Yes, this was useful">👍</button>
                  <button onClick={() => handleRateUsefulness(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px' }} title="No, this wasn't useful">👎</button>
                </span>
              ) : (
                <span>{usefulnessRated ? 'Thanks for the feedback!' : "Thanks — we'll keep improving this."}</span>
              )}
            </span>
          </div>
          <button
            onClick={handleAnalyze}
            disabled={loading}
            className="btn-regenerate"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Regenerating...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{marginRight: '6px'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Regenerate Analysis
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RCAAnalysisTab;
